import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import MobileSelectField from "../../../components/mobile/MobileSelectField";
import {
  BRAND,
  primaryButtonSx,
  inputSx,
  mobileInputSx,
} from "../../../components/auth/authTheme";
import { MobileFormFooter } from "../profileViewHelpers";

const genderOptions = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
];

const SectionCard = ({ title, icon, isMobile, children }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: isMobile ? "14px" : 0,
      border: isMobile ? `1px solid ${BRAND.border}` : "none",
      p: isMobile ? 1.5 : 0,
      mb: isMobile ? 1.5 : 2,
      boxShadow: isMobile ? "0 1px 6px rgba(26, 43, 74, 0.04)" : "none",
    }}
  >
    {title && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
        {icon && (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              bgcolor: BRAND.orangeLight,
              color: BRAND.orangeHover,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        )}
        <Typography sx={{ fontWeight: 700, color: BRAND.navy, fontSize: "0.9375rem" }}>
          {title}
        </Typography>
      </Box>
    )}
    {children}
  </Box>
);

const formatDateForInput = (value) => {
  if (!value) return "";
  const str = String(value);
  return str.includes("T") ? str.split("T")[0] : str.slice(0, 10);
};

const BasicDetailEditForm = ({ userDetails, onCancel, onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fieldSx = isMobile ? mobileInputSx : inputSx;
  const gridSpacing = isMobile ? 1.5 : 2;

  const [initialValues, setInitialValues] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile_number: "",
    gender: "",
    nationality: "",
    date_of_birth: "",
    aadhar_number: "",
    residentialAddress: "",
    currentAddress: "",
    about: "",
  });

  useEffect(() => {
    if (userDetails) {
      setInitialValues({
        first_name: userDetails.first_name || "",
        last_name: userDetails.last_name || "",
        email: userDetails.email || "",
        mobile_number: userDetails.mobile_number || "",
        gender: userDetails.gender || "",
        nationality: userDetails.nationality || "",
        date_of_birth: formatDateForInput(userDetails.profile_data?.date_of_birth),
        aadhar_number: userDetails.profile_data?.aadhar_number || "",
        residentialAddress:
          userDetails.profile_data?.address?.residentialAddress || "",
        currentAddress: userDetails.profile_data?.address?.currentAddress || "",
        about: userDetails.profile_data?.about || "",
      });
    }
  }, [userDetails]);

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required("Required"),
    last_name: Yup.string().required("Required"),
    email: Yup.string().email("Invalid email").required("Required"),
    mobile_number: Yup.string().required("Required"),
  });

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSave}
    >
      {({ values, handleChange, setFieldValue, touched, errors, isSubmitting }) => (
        <Form>
          <Grid container spacing={gridSpacing} sx={{ ml: 0, width: "100%" }}>
            <Grid item xs={12}>
              <SectionCard
                title="Personal Information"
                icon={<PersonOutlinedIcon sx={{ fontSize: 16 }} />}
                isMobile={isMobile}
              >
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name *"
                      name="first_name"
                      value={values.first_name}
                      onChange={handleChange}
                      error={touched.first_name && Boolean(errors.first_name)}
                      helperText={touched.first_name && errors.first_name}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name *"
                      name="last_name"
                      value={values.last_name}
                      onChange={handleChange}
                      error={touched.last_name && Boolean(errors.last_name)}
                      helperText={touched.last_name && errors.last_name}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email *"
                      name="email"
                      type="email"
                      value={values.email}
                      onChange={handleChange}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Mobile Number *"
                      name="mobile_number"
                      value={values.mobile_number}
                      onChange={handleChange}
                      error={touched.mobile_number && Boolean(errors.mobile_number)}
                      helperText={touched.mobile_number && errors.mobile_number}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {isMobile ? (
                      <MobileSelectField
                        label="Gender"
                        value={values.gender ?? ""}
                        onChange={(val) => setFieldValue("gender", val)}
                        options={genderOptions}
                        sheetTitle="Select gender"
                      />
                    ) : (
                      <TextField
                        select
                        fullWidth
                        label="Gender"
                        name="gender"
                        value={values.gender ?? ""}
                        onChange={handleChange}
                        sx={fieldSx}
                      >
                        <MenuItem value="">Select</MenuItem>
                        {genderOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nationality"
                      name="nationality"
                      value={values.nationality}
                      onChange={handleChange}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Date of Birth"
                      name="date_of_birth"
                      type="date"
                      value={values.date_of_birth}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Aadhar Number"
                      name="aadhar_number"
                      value={values.aadhar_number}
                      onChange={handleChange}
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>

            <Grid item xs={12}>
              <SectionCard
                title="Address & About"
                icon={<HomeOutlinedIcon sx={{ fontSize: 16 }} />}
                isMobile={isMobile}
              >
                <Grid container spacing={gridSpacing}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Residential Address"
                      name="residentialAddress"
                      value={values.residentialAddress}
                      onChange={handleChange}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Current Address"
                      name="currentAddress"
                      value={values.currentAddress}
                      onChange={handleChange}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={isMobile ? 4 : 3}
                      label="About"
                      name="about"
                      value={values.about}
                      onChange={handleChange}
                      placeholder="Tell us a little about yourself..."
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>

            <Grid item xs={12}>
              {isMobile ? (
                <MobileFormFooter onCancel={onCancel} isSubmitting={isSubmitting} />
              ) : (
                <Box display="flex" justifyContent="flex-end" gap={2} mt={1}>
                  <Button variant="outlined" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button variant="contained" type="submit" sx={primaryButtonSx}>
                    Save
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default BasicDetailEditForm;
